import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { AsciiEffect } from './ascii.ts';
import SolverWorker from './solver-worker.ts?worker';

// ─── Solver worker (shared across instances) ──────────────────────────
let solverWorker: Worker | null = null;
let solverReady = false;
let solverReadyPromise: Promise<void> | null = null;
let solveResolve: ((solution: string) => void) | null = null;
let solveReject: (() => void) | null = null;

function ensureWorker(): void {
  if (solverWorker) return;
  solverWorker = new SolverWorker();
  solverReadyPromise = new Promise<void>(resolve => {
    solverWorker!.addEventListener('message', function onReady(e) {
      if (e.data.type === 'ready') {
        solverReady = true;
        solverWorker!.removeEventListener('message', onReady);
        resolve();
      }
    });
  });
  solverWorker.addEventListener('message', (e) => {
    if (e.data.type === 'solution') {
      solveResolve?.(e.data.solution);
      solveResolve = solveReject = null;
    } else if (e.data.type === 'error') {
      solveReject?.();
      solveResolve = solveReject = null;
    }
  });
  solverWorker.postMessage({ type: 'init' });
}

// ─── Utils (pure, exported) ────────────────────────────────────────────
export function hslToCSS(h: number, s: number, l: number): string {
  return `hsl(${h.toFixed(1)}, ${(s * 100).toFixed(1)}%, ${(l * 100).toFixed(1)}%)`;
}

export function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r: number, g: number, b: number;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else[r, g, b] = [c, 0, x];
  const hex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

// ─── Types ─────────────────────────────────────────────────────────────
type Axis = 'x' | 'y' | 'z';
interface Move { axis: Axis; layer: number; dir: number; }
interface HitInfo {
  point: THREE.Vector3;
  faceNormal: THREE.Vector3;
  cubieGrid: { x: number; y: number; z: number };
}
interface ActiveDrag {
  axis: Axis; layer: number; dc: 'x' | 'y';
  sign: number; sens: number; angle: number;
}
type InteractionMode = 'face-pending' | 'face-dragging' | null;

export interface RubiksCubeOptions {
  asciiCanvas: HTMLCanvasElement;
  faceColors: string[];           // 6 hex strings (or pass palette via hslToHex)
  labelElements?: Array<HTMLElement | null>; // length 6, optional
  asciiResolution?: number;       // default 0.05
  cameraPosition?: [number, number, number];
  bodySize?: number;
  bodyRadius?: number;
  stickerSize?: number;
  stickerRadius?: number;
  stickerLift?: number;
  gap?: number;
  scrambleCount?: number;
  scrambleMoveDuration?: number;
  resetMoveDuration?: number;
  snapDuration?: number;
  /** ASCII canvas background fill. Pass 'transparent' for none. Default: 'transparent'. */
  asciiBg?: string;
  /** Per-cell background disc behind each character. Pass 'transparent' for none. Default: 'transparent'. */
  cellBg?: string;
  palette?: string[];
}

// ─── Geometry helpers (module-private) ─────────────────────────────────
function makeRoundedBox(size: number, radius: number, seg: number): THREE.BoxGeometry {
  const geo = new THREE.BoxGeometry(size, size, size, seg, seg, seg);
  const pos = geo.attributes.position;
  const inner = size / 2 - radius;
  for (let i = 0; i < pos.count; i++) {
    const vx = pos.getX(i), vy = pos.getY(i), vz = pos.getZ(i);
    const cx = Math.max(-inner, Math.min(inner, vx));
    const cy = Math.max(-inner, Math.min(inner, vy));
    const cz = Math.max(-inner, Math.min(inner, vz));
    const dx = vx - cx, dy = vy - cy, dz = vz - cz;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d > 1e-6) pos.setXYZ(i, cx + (dx / d) * radius, cy + (dy / d) * radius, cz + (dz / d) * radius);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

function makeRoundedRectShape(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const hw = w / 2, hh = h / 2;
  s.moveTo(-hw + r, -hh);
  s.lineTo(hw - r, -hh); s.quadraticCurveTo(hw, -hh, hw, -hh + r);
  s.lineTo(hw, hh - r); s.quadraticCurveTo(hw, hh, hw - r, hh);
  s.lineTo(-hw + r, hh); s.quadraticCurveTo(-hw, hh, -hw, hh - r);
  s.lineTo(-hw, -hh + r); s.quadraticCurveTo(-hw, -hh, -hw + r, -hh);
  return s;
}

// ─── Class ─────────────────────────────────────────────────────────────
export class RubiksCube {
  // config
  private readonly BODY_SIZE: number;
  private readonly BODY_RADIUS: number;
  private readonly STICKER_SIZE: number;
  private readonly STICKER_RADIUS: number;
  private readonly STICKER_LIFT: number;
  private readonly GAP: number;
  private readonly scrambleCount: number;
  private readonly scrambleMoveDur: number;
  private readonly resetMoveDur: number;
  private readonly snapDur: number;

  // three
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private asciiEffect: AsciiEffect;
  private asciiCanvas: HTMLCanvasElement;
  private controls: OrbitControls;
  private pivot = new THREE.Group();
  private interactionElement: HTMLElement;

  // cube data
  private faceColors: string[];
  private cubies: THREE.Group[] = [];
  private bodyGeo: THREE.BoxGeometry | null = null;
  private stickerGeo: THREE.ShapeGeometry;
  private stickerConfigs: Array<{ pos: [number, number, number]; rot: [number, number, number] }>;
  private faceNormals: THREE.Vector3[];
  private bodyMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
  private centerCubies: Array<THREE.Group | null> = [null, null, null, null, null, null];
  private labelElements: Array<HTMLElement | null>;

  // state
  private animatingSnap = false;
  private scrambling = false;
  private solving = false;

  // interaction
  private interactionMode: InteractionMode = null;
  private dragStart: { x: number; y: number } | null = null;
  private hitInfo: HitInfo | null = null;
  private activeDrag: ActiveDrag | null = null;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();

  // loop
  private rafId: number | null = null;
  private resizeObs: ResizeObserver;

  // bound handlers (so we can remove them)
  private _onDown = (e: PointerEvent) => this.onDown(e);
  private _onMove = (e: PointerEvent) => this.onMove(e);
  private _onUp = () => this.onUp();

  // label scratch
  private _lwp = new THREE.Vector3();
  private _lsp = new THREE.Vector3();
  private _lwn = new THREE.Vector3();
  private _cdir = new THREE.Vector3();

  constructor(opts: RubiksCubeOptions) {
    this.BODY_SIZE = opts.bodySize ?? 0.9;
    this.BODY_RADIUS = opts.bodyRadius ?? 0.12;
    this.STICKER_SIZE = opts.stickerSize ?? 0.76;
    this.STICKER_RADIUS = opts.stickerRadius ?? 0.1;
    this.STICKER_LIFT = opts.stickerLift ?? 0.005;
    this.GAP = opts.gap ?? 1.06;
    this.scrambleCount = opts.scrambleCount ?? 20;
    this.scrambleMoveDur = opts.scrambleMoveDuration ?? 120;
    this.resetMoveDur = opts.resetMoveDuration ?? 60;
    this.snapDur = opts.snapDuration ?? 150;

    this.asciiCanvas = opts.asciiCanvas;
    this.faceColors = opts.faceColors.slice(0, 6);
    this.labelElements = opts.labelElements ?? [null, null, null, null, null, null];

    // static face data
    const HB = this.BODY_SIZE / 2 + this.STICKER_LIFT;
    this.stickerConfigs = [
      { pos: [HB, 0, 0], rot: [0, Math.PI / 2, 0] },
      { pos: [-HB, 0, 0], rot: [0, -Math.PI / 2, 0] },
      { pos: [0, HB, 0], rot: [-Math.PI / 2, 0, 0] },
      { pos: [0, -HB, 0], rot: [Math.PI / 2, 0, 0] },
      { pos: [0, 0, HB], rot: [0, 0, 0] },
      { pos: [0, 0, -HB], rot: [0, Math.PI, 0] },
    ];
    this.faceNormals = [
      new THREE.Vector3(1, 0, 0), new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -1),
    ];
    this.stickerGeo = new THREE.ShapeGeometry(
      makeRoundedRectShape(this.STICKER_SIZE, this.STICKER_SIZE, this.STICKER_RADIUS), 8
    );

    // renderer / camera / ascii
    this.camera = new THREE.PerspectiveCamera(40, innerWidth / innerHeight, 0.1, 100);
    const cp = opts.cameraPosition ?? [6, 6, 6];
    this.camera.position.set(cp[0], cp[1], cp[2]);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(1);
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.style.display = 'none';
    document.body.appendChild(this.renderer.domElement);

    this.asciiEffect = new AsciiEffect(this.renderer, {
      resolution: opts.asciiResolution ?? 0.15,
      color: true,
      canvas: this.asciiCanvas,
    });
    this.asciiEffect._bgCSS = opts.asciiBg ?? 'transparent';
    this.asciiEffect._cellBgCSS = opts.cellBg ?? '#ffffff';
    this.asciiEffect.domElement.addEventListener('pointermove', (e) => {
      const rect = this.asciiEffect.domElement.getBoundingClientRect();
      this.asciiEffect.cursorX = (e.clientX - rect.left) * (this.asciiEffect.domElement.width / rect.width);
      this.asciiEffect.cursorY = (e.clientY - rect.top) * (this.asciiEffect.domElement.height / rect.height);
    });
    this.asciiEffect.domElement.addEventListener('pointerleave', () => {
      this.asciiEffect.cursorX = -9999;
      this.asciiEffect.cursorY = -9999;
    });

    this.asciiEffect.palette = opts.palette || [];
    this.interactionElement = this.asciiEffect.domElement;

    this.controls = new OrbitControls(this.camera, this.interactionElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.enablePan = false;
    this.controls.minDistance = 4;
    this.controls.maxDistance = 14;
    this.controls.target.set(0, 0, 0);
    this.controls.update();

    // lights
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const d1 = new THREE.DirectionalLight(0xffffff, 0.6); d1.position.set(5, 8, 6); this.scene.add(d1);
    const d2 = new THREE.DirectionalLight(0xffffff, 0.2); d2.position.set(-4, -2, -5); this.scene.add(d2);
    this.scene.add(this.pivot);

    // resize
    this.resizeObs = new ResizeObserver(() => this.resize());
    this.resizeObs.observe(this.asciiCanvas);
    this.resize();

    // interaction
    this.interactionElement.addEventListener('pointerdown', this._onDown as EventListener);
    this.interactionElement.addEventListener('pointermove', this._onMove as EventListener);
    this.interactionElement.addEventListener('pointerup', this._onUp);
    this.interactionElement.addEventListener('pointerleave', this._onUp);

    this.createCube();
  }

  // ─── Public API ──────────────────────────────────────────────────────
  start(): void {
    if (this.rafId !== null) return;
    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      this.controls.update();
      this.asciiEffect.render(this.scene, this.camera);
      this.updateLabels();
    };
    loop();
  }

  stop(): void {
    if (this.rafId !== null) { cancelAnimationFrame(this.rafId); this.rafId = null; }
  }

  setFaceColors(colors: string[]): void {
    this.faceColors = colors.slice(0, 6);
    this.createCube();
  }

  /** Set the background fill for the entire ASCII canvas. Pass 'transparent' for none. */
  setAsciiBg(css: string): void {
    this.asciiEffect._bgCSS = css;
  }

  /** Rotate the camera around the cube by yaw radians (azimuthal). */
  rotateView(yaw: number): void {
    const offset = this.camera.position.clone().sub(this.controls.target);
    const y = offset.y;                                      // preserve elevation
    const horizRadius = Math.sqrt(offset.x * offset.x + offset.z * offset.z);
    const currentAngle = Math.atan2(offset.x, offset.z);
    const newAngle = currentAngle + yaw;
    offset.x = Math.sin(newAngle) * horizRadius;
    offset.z = Math.cos(newAngle) * horizRadius;
    offset.y = y;
    this.camera.position.copy(this.controls.target).add(offset);
    this.camera.lookAt(this.controls.target);
    this.controls.update();
  }

  /** Set the per-cell background disc color behind each character. Pass 'transparent' for none. */
  setCellBg(css: string): void {
    this.asciiEffect._cellBgCSS = css;
    this.asciiEffect.rebuildCellBg();
  }

  scramble(): void {
    if (this.animatingSnap || this.scrambling || this.solving) return;
    this.scrambling = true;
    ensureWorker();
    const axes: Axis[] = ['x', 'y', 'z'];
    const layers = [-1, 0, 1], dirs = [-1, 1];
    const moves: Move[] = [];
    for (let i = 0; i < this.scrambleCount; i++) {
      moves.push({
        axis: axes[Math.floor(Math.random() * 3)],
        layer: layers[Math.floor(Math.random() * 3)],
        dir: dirs[Math.floor(Math.random() * 2)],
      });
    }
    let i = 0;
    const next = () => {
      if (i >= moves.length) { this.scrambling = false; return; }
      const m = moves[i++];
      this.doMoveAnimated(m.axis, m.layer, m.dir, this.scrambleMoveDur, next);
    };
    next();
  }

  async solve(): Promise<void> {
    if (this.animatingSnap || this.scrambling || this.solving) return;
    const state = this.extractState();
    const solved = 'UUUUUUUUURRRRRRRRFFFFFFFFFDDDDDDDDDLLLLLLLLLBBBBBBBBB';
    if (state === solved) return;
    this.solving = true;
    ensureWorker();

    const solveIcon = document.getElementById('solve-icon');
    const solveSpinner = document.getElementById('solve-spinner');
    const showLoader = () => {
      solveIcon?.classList.add('hidden');
      solveSpinner?.classList.remove('hidden');
    };
    const hideLoader = () => {
      solveSpinner?.classList.add('hidden');
      solveIcon?.classList.remove('hidden');
    };

    if (!solverReady) showLoader();

    try {
      await solverReadyPromise;
      hideLoader();

      const solution = await new Promise<string>((resolve, reject) => {
        solveResolve = resolve;
        solveReject = reject;
        solverWorker!.postMessage({ type: 'solve', state });
      });

      const moves = this.parseSolution(solution);
      let i = 0;
      const next = () => {
        if (i >= moves.length) { this.solving = false; return; }
        const m = moves[i++];
        this.doMoveAnimated(m.axis, m.layer, m.dir, this.resetMoveDur, next);
      };
      next();
    } catch {
      hideLoader();
      this.solving = false;
    }
  }

  dispose(): void {
    this.stop();
    this.resizeObs.disconnect();
    this.interactionElement.removeEventListener('pointerdown', this._onDown as EventListener);
    this.interactionElement.removeEventListener('pointermove', this._onMove as EventListener);
    this.interactionElement.removeEventListener('pointerup', this._onUp);
    this.interactionElement.removeEventListener('pointerleave', this._onUp);
    this.cubies.forEach(g => this.scene.remove(g));
    this.stickerGeo.dispose();
    this.bodyGeo?.dispose();
    this.bodyMat.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  // ─── Internals ───────────────────────────────────────────────────────
  private resize(): void {
    const rect = this.asciiCanvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    this.asciiEffect.setSize(w, h);

    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    // Fit the cube in view regardless of aspect
    const R = 2.6; // cube bounding radius — tune if cube looks too small/big
    const vFov = this.camera.fov * Math.PI / 180;
    const distForHeight = R / Math.tan(vFov / 2);
    const distForWidth = R / (Math.tan(vFov / 2) * this.camera.aspect);
    const dist = Math.max(distForHeight, distForWidth) * 1.5;

    // Move the camera to `dist` from the target along its current direction
    const dir = this.camera.position.clone().sub(this.controls.target).normalize();
    this.camera.position.copy(this.controls.target).addScaledVector(dir, dist);
    this.controls.update();
  }

  private createCube(): void {
    this.labelElements.forEach(el => {
      if (el) { el.style.left = '0px'; el.style.top = '0px'; }
    });
    this.cubies.forEach(g => this.scene.remove(g));
    this.cubies = [];
    this.pivot.rotation.set(0, 0, 0);
    while (this.pivot.children.length) this.pivot.remove(this.pivot.children[0]);
    this.centerCubies = [null, null, null, null, null, null];

    this.bodyGeo?.dispose();
    this.bodyGeo = makeRoundedBox(this.BODY_SIZE, this.BODY_RADIUS, 10);
    const mats = this.faceColors.map(hex => new THREE.MeshBasicMaterial({ color: hex }));

    for (let x = -1; x <= 1; x++) for (let y = -1; y <= 1; y++) for (let z = -1; z <= 1; z++) {
      const g = new THREE.Group();
      g.position.set(x * this.GAP, y * this.GAP, z * this.GAP);
      g.add(new THREE.Mesh(this.bodyGeo, this.bodyMat));
      for (let fi = 0; fi < 6; fi++) {
        const sc = this.stickerConfigs[fi];
        const st = new THREE.Mesh(this.stickerGeo, mats[fi]);
        st.position.set(...sc.pos); st.rotation.set(...sc.rot);
        st.userData.faceIndex = fi; st.userData.cubieGroup = g;
        g.add(st);
      }
      this.scene.add(g);
      this.cubies.push(g);
      if (x === 1 && y === 0 && z === 0) this.centerCubies[0] = g;
      if (x === -1 && y === 0 && z === 0) this.centerCubies[1] = g;
      if (x === 0 && y === 1 && z === 0) this.centerCubies[2] = g;
      if (x === 0 && y === -1 && z === 0) this.centerCubies[3] = g;
      if (x === 0 && y === 0 && z === 1) this.centerCubies[4] = g;
      if (x === 0 && y === 0 && z === -1) this.centerCubies[5] = g;
    }
  }

  private getCubiesInSlice(axis: Axis, layer: number): THREE.Group[] {
    const eps = 0.3;
    return this.cubies.filter(c => {
      const p = new THREE.Vector3(); c.getWorldPosition(p);
      const v = axis === 'x' ? p.x : axis === 'y' ? p.y : p.z;
      return Math.abs(v - layer * this.GAP) < eps;
    });
  }

  private attachSliceToPivot(axis: Axis, layer: number): void {
    this.pivot.rotation.set(0, 0, 0);
    this.pivot.updateMatrixWorld();
    this.getCubiesInSlice(axis, layer).forEach(c => { this.scene.remove(c); this.pivot.add(c); });
  }

  private detachPivot(): void {
    this.pivot.updateMatrixWorld();
    [...this.pivot.children].forEach(c => {
      c.applyMatrix4(this.pivot.matrixWorld);
      this.pivot.remove(c);
      c.position.x = Math.round(c.position.x / this.GAP) * this.GAP;
      c.position.y = Math.round(c.position.y / this.GAP) * this.GAP;
      c.position.z = Math.round(c.position.z / this.GAP) * this.GAP;
      this.scene.add(c);
    });
    this.pivot.rotation.set(0, 0, 0);
  }

  private setPivotRotation(axis: Axis, a: number): void {
    if (axis === 'x') this.pivot.rotation.x = a;
    else if (axis === 'y') this.pivot.rotation.y = a;
    else this.pivot.rotation.z = a;
  }

  private getPivotRotation(axis: Axis): number {
    if (axis === 'x') return this.pivot.rotation.x;
    if (axis === 'y') return this.pivot.rotation.y;
    return this.pivot.rotation.z;
  }

  private snapAnimate(axis: Axis, target: number, dur: number, cb?: () => void): void {
    this.animatingSnap = true;
    const start = this.getPivotRotation(axis);
    const delta = target - start;
    const t0 = performance.now();
    const tick = () => {
      let t = Math.min((performance.now() - t0) / dur, 1);
      t = 1 - Math.pow(1 - t, 3);
      this.setPivotRotation(axis, start + delta * t);
      if (t < 1) requestAnimationFrame(tick);
      else { this.detachPivot(); this.animatingSnap = false; cb?.(); }
    };
    requestAnimationFrame(tick);
  }

  private doMoveAnimated(axis: Axis, layer: number, dir: number, dur: number, cb?: () => void): void {
    if (this.animatingSnap) return;
    this.attachSliceToPivot(axis, layer);
    this.setPivotRotation(axis, 0);
    this.snapAnimate(axis, (dir * Math.PI) / 2, dur, cb);
  }

  private extractState(): string {
    // Solver face order: U, R, F, D, L, B
    // Outward normals for each solver face
    const faceOutwardNormals = [
      new THREE.Vector3(0, 1, 0),   // U = +Y
      new THREE.Vector3(1, 0, 0),   // R = +X
      new THREE.Vector3(0, 0, 1),   // F = +Z
      new THREE.Vector3(0, -1, 0),  // D = -Y
      new THREE.Vector3(-1, 0, 0),  // L = -X
      new THREE.Vector3(0, 0, -1),  // B = -Z
    ];
    const faceLabels = ['U', 'R', 'F', 'D', 'L', 'B'];

    // Grid positions for each face in Kociemba facelet order (row-major, looking from outside)
    const facePositions: [number, number, number][][] = [
      // U (y=1): back-to-front rows, left-to-right columns
      [[-1,1,-1],[0,1,-1],[1,1,-1],[-1,1,0],[0,1,0],[1,1,0],[-1,1,1],[0,1,1],[1,1,1]],
      // R (x=1): top-to-bottom, left(+z) to right(-z)
      [[1,1,1],[1,1,0],[1,1,-1],[1,0,1],[1,0,0],[1,0,-1],[1,-1,1],[1,-1,0],[1,-1,-1]],
      // F (z=1): top-to-bottom, left(-x) to right(+x)
      [[-1,1,1],[0,1,1],[1,1,1],[-1,0,1],[0,0,1],[1,0,1],[-1,-1,1],[0,-1,1],[1,-1,1]],
      // D (y=-1): front-to-back rows, left-to-right columns
      [[-1,-1,1],[0,-1,1],[1,-1,1],[-1,-1,0],[0,-1,0],[1,-1,0],[-1,-1,-1],[0,-1,-1],[1,-1,-1]],
      // L (x=-1): top-to-bottom, left(-z) to right(+z)
      [[-1,1,-1],[-1,1,0],[-1,1,1],[-1,0,-1],[-1,0,0],[-1,0,1],[-1,-1,-1],[-1,-1,0],[-1,-1,1]],
      // B (z=-1): top-to-bottom, left(+x) to right(-x)
      [[1,1,-1],[0,1,-1],[-1,1,-1],[1,0,-1],[0,0,-1],[-1,0,-1],[1,-1,-1],[0,-1,-1],[-1,-1,-1]],
    ];

    // Center positions for each solver face
    const centerPositions: [number, number, number][] = [
      [0, 1, 0], [1, 0, 0], [0, 0, 1], [0, -1, 0], [-1, 0, 0], [0, 0, -1],
    ];

    // Build faceIndex → face label mapping from center cubies
    const faceIndexToLabel: Record<number, string> = {};
    for (let fi = 0; fi < 6; fi++) {
      const [cx, cy, cz] = centerPositions[fi];
      const normal = faceOutwardNormals[fi];
      const cubie = this.findCubieAt(cx, cy, cz);
      if (!cubie) continue;
      const fIdx = this.getOutwardFaceIndex(cubie, normal);
      faceIndexToLabel[fIdx] = faceLabels[fi];
    }

    // For each face, read 9 facelets
    let result = '';
    for (let fi = 0; fi < 6; fi++) {
      const normal = faceOutwardNormals[fi];
      for (const [gx, gy, gz] of facePositions[fi]) {
        const cubie = this.findCubieAt(gx, gy, gz);
        if (!cubie) { result += '?'; continue; }
        const fIdx = this.getOutwardFaceIndex(cubie, normal);
        result += faceIndexToLabel[fIdx] ?? '?';
      }
    }
    return result;
  }

  private findCubieAt(gx: number, gy: number, gz: number): THREE.Group | null {
    const eps = 0.3;
    const tx = gx * this.GAP, ty = gy * this.GAP, tz = gz * this.GAP;
    const p = new THREE.Vector3();
    for (const c of this.cubies) {
      c.getWorldPosition(p);
      if (Math.abs(p.x - tx) < eps && Math.abs(p.y - ty) < eps && Math.abs(p.z - tz) < eps) {
        return c;
      }
    }
    return null;
  }

  private getOutwardFaceIndex(cubie: THREE.Group, outwardNormal: THREE.Vector3): number {
    let bestDot = -Infinity;
    let bestIdx = 0;
    const transformed = new THREE.Vector3();
    const worldQuat = new THREE.Quaternion();
    cubie.getWorldQuaternion(worldQuat);
    for (const child of cubie.children) {
      const fi = child.userData.faceIndex as number | undefined;
      if (fi === undefined) continue;
      transformed.copy(this.faceNormals[fi]).applyQuaternion(worldQuat);
      const dot = transformed.dot(outwardNormal);
      if (dot > bestDot) { bestDot = dot; bestIdx = fi; }
    }
    return bestIdx;
  }

  private parseSolution(solution: string): Move[] {
    if (!solution || solution.trim() === '') return [];
    const moves: Move[] = [];
    const tokens = solution.trim().split(/\s+/);
    // CW dir is negative for positive-axis layers, positive for negative-axis layers
    const faceMap: Record<string, { axis: Axis; layer: number; cwDir: number }> = {
      'U': { axis: 'y', layer: 1, cwDir: -1 },
      'D': { axis: 'y', layer: -1, cwDir: 1 },
      'R': { axis: 'x', layer: 1, cwDir: -1 },
      'L': { axis: 'x', layer: -1, cwDir: 1 },
      'F': { axis: 'z', layer: 1, cwDir: -1 },
      'B': { axis: 'z', layer: -1, cwDir: 1 },
    };
    for (const token of tokens) {
      const face = token[0];
      const fm = faceMap[face];
      if (!fm) continue;
      const suffix = token.slice(1);
      if (suffix === '2') {
        moves.push({ axis: fm.axis, layer: fm.layer, dir: fm.cwDir * 2 });
      } else if (suffix === "'") {
        moves.push({ axis: fm.axis, layer: fm.layer, dir: -fm.cwDir });
      } else {
        moves.push({ axis: fm.axis, layer: fm.layer, dir: fm.cwDir });
      }
    }
    return moves;
  }

  private onDown(e: PointerEvent): void {
    if (this.animatingSnap) return;
    const pos = { x: e.clientX, y: e.clientY };
    this.dragStart = pos;
    this.mouse.x = (pos.x / innerWidth) * 2 - 1;
    this.mouse.y = -(pos.y / innerHeight) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const meshes: THREE.Mesh[] = [];
    this.cubies.forEach(g => g.children.forEach(c => { if ((c as THREE.Mesh).isMesh) meshes.push(c as THREE.Mesh); }));
    const hits = this.raycaster.intersectObjects(meshes);

    if (hits.length > 0) {
      let sh: THREE.Intersection | null = null;
      for (const h of hits) if (h.object.userData.cubieGroup) { sh = h; break; }
      const hit = sh ?? hits[0];
      const cg = (hit.object.userData.cubieGroup as THREE.Group | undefined) ?? (hit.object.parent as THREE.Group);
      this.interactionMode = 'face-pending';
      this.controls.enabled = false;
      const wn = hit.face!.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
      wn.x = Math.round(wn.x); wn.y = Math.round(wn.y); wn.z = Math.round(wn.z);
      this.hitInfo = {
        point: hit.point.clone(), faceNormal: wn,
        cubieGrid: {
          x: Math.round(cg.position.x / this.GAP),
          y: Math.round(cg.position.y / this.GAP),
          z: Math.round(cg.position.z / this.GAP),
        },
      };
    } else {
      this.interactionMode = null;
      this.hitInfo = null;
    }
    this.activeDrag = null;
  }

  private onMove(e: PointerEvent): void {
    if (!this.dragStart || !this.interactionMode) return;
    const pos = { x: e.clientX, y: e.clientY };
    const dx = pos.x - this.dragStart.x, dy = pos.y - this.dragStart.y;

    if (this.interactionMode === 'face-pending' && !this.animatingSnap && Math.hypot(dx, dy) > 12) {
      const info = this.determineDragAxis(dx, dy);
      if (info) {
        this.activeDrag = info;
        this.attachSliceToPivot(info.axis, info.layer);
        this.interactionMode = 'face-dragging';
        this.dragStart = pos;
      }
      return;
    }

    if (this.interactionMode === 'face-dragging' && this.activeDrag && !this.animatingSnap) {
      const sd = this.activeDrag.dc === 'x' ? pos.x - this.dragStart.x : -(pos.y - this.dragStart.y);
      this.activeDrag.angle += sd * this.activeDrag.sens * this.activeDrag.sign;
      this.setPivotRotation(this.activeDrag.axis, this.activeDrag.angle);
      this.dragStart = pos;
    }
  }

  private onUp(): void {
    if (this.interactionMode === 'face-dragging' && this.activeDrag && !this.animatingSnap) {
      const cur = this.getPivotRotation(this.activeDrag.axis);
      const snapped = Math.round(cur / (Math.PI / 2)) * (Math.PI / 2);
      this.snapAnimate(this.activeDrag.axis, snapped, this.snapDur);
    }
    this.controls.enabled = true;
    this.dragStart = null;
    this.interactionMode = null;
    this.hitInfo = null;
    this.activeDrag = null;
  }

  private determineDragAxis(dx: number, dy: number): ActiveDrag | null {
    if (!this.hitInfo) return null;
    const fn = this.hitInfo.faceNormal, cg = this.hitInfo.cubieGrid;
    let t1: THREE.Vector3, t2: THREE.Vector3;
    if (Math.abs(fn.x) > 0.5) { t1 = new THREE.Vector3(0, 1, 0); t2 = new THREE.Vector3(0, 0, 1); }
    else if (Math.abs(fn.y) > 0.5) { t1 = new THREE.Vector3(1, 0, 0); t2 = new THREE.Vector3(0, 0, 1); }
    else { t1 = new THREE.Vector3(1, 0, 0); t2 = new THREE.Vector3(0, 1, 0); }

    const p0 = this.hitInfo.point.clone().project(this.camera);
    const p1 = this.hitInfo.point.clone().add(t1.clone().multiplyScalar(0.5)).project(this.camera);
    const p2 = this.hitInfo.point.clone().add(t2.clone().multiplyScalar(0.5)).project(this.camera);
    const st1 = new THREE.Vector2(p1.x - p0.x, p1.y - p0.y);
    const st2 = new THREE.Vector2(p2.x - p0.x, p2.y - p0.y);
    if (st1.length() > 0.001) st1.normalize();
    if (st2.length() > 0.001) st2.normalize();
    const dd = new THREE.Vector2(dx, -dy);
    if (dd.length() > 0.001) dd.normalize();
    const d1 = Math.abs(dd.dot(st1)), d2 = Math.abs(dd.dot(st2));
    const tang = d1 > d2 ? t1 : t2, st = d1 > d2 ? st1 : st2;
    const ra = new THREE.Vector3().crossVectors(fn, tang).round();
    const axis: Axis = Math.abs(ra.x) > 0.5 ? 'x' : Math.abs(ra.y) > 0.5 ? 'y' : 'z';
    const axisSign = (ra as unknown as Record<Axis, number>)[axis] > 0 ? 1 : -1;
    const layer = axis === 'x' ? cg.x : axis === 'y' ? cg.y : cg.z;
    const dc: 'x' | 'y' = Math.abs(st.x) > Math.abs(st.y) ? 'x' : 'y';
    const rc = dc === 'x' ? st.x : st.y;
    return { axis, layer, dc, sign: (rc > 0 ? 1 : -1) * axisSign, sens: 0.008, angle: 0 };
  }

  private updateLabels(): void {
    const rect = this.asciiCanvas.getBoundingClientRect();
    for (let i = 0; i < 6; i++) {
      const el = this.labelElements[i], cubie = this.centerCubies[i];
      if (!el) continue;
      if (!cubie) { el.classList.remove('visible'); el.style.opacity = ''; continue; }

      cubie.getWorldPosition(this._lwp);
      this._lwn.copy(this.faceNormals[i]).transformDirection(cubie.matrixWorld);
      this._cdir.copy(this.camera.position).sub(this._lwp).normalize();

      const dot = this._lwn.dot(this._cdir);
      if (dot < 0.15) {
        el.classList.remove('visible');
        el.style.opacity = '';
        if (el.dataset.locked === 'true') {
          el.dispatchEvent(new CustomEvent('label:offscreen'));
        }
        continue;
      }
      this._lsp.copy(this._lwp).addScaledVector(this._lwn, this.BODY_SIZE * 0.52).project(this.camera);

      const px = rect.left + (this._lsp.x * 0.5 + 0.5) * rect.width;
      const py = rect.top + (-this._lsp.y * 0.5 + 0.5) * rect.height;
      el.style.transform = `translate(${px}px, ${py}px) translate(-50%, -50%)`;

      el.classList.add('visible');
      const v = Math.min(1, (dot - 0.05) / 0.4);
      el.style.opacity = String(v);
      const btnChild = el.querySelector<HTMLElement>('button, [data-blur-target]');
      if (btnChild) btnChild.style.filter = `blur(${(1 - v) * 6}px)`;
    }
  }
}
